/**
 * Redaction of credentials before anything is sent to PostHog.
 *
 * Magic link tokens, invitation tokens, email confirmation keys and SMS codes all
 * travel in URL paths and query strings, and PostHog records the URL on every
 * pageview, autocapture and `$referrer`. Wired up as `before_send` in index.tsx so
 * no capture path can bypass it.
 *
 * The Django side mirrors these rules in strongmsp_app/analytics/sanitize.py; keep
 * the two lists in sync when a new token-bearing route is added.
 */

export const REDACTED = "[redacted]";

/**
 * A path segment that names a credential: whatever follows it is the credential.
 * e.g. /auth/magic-link/<token>/, /invite/<token>, /account/password/reset/key/<key>
 */
const SECRET_PATH_SEGMENTS = new Set([
  "magic-link",
  "invite",
  "verify-email",
  "confirm-email",
  "key",
  "token",
  "sesame"
]);

/** Query parameters whose value is a credential or personal identifier. */
const SECRET_QUERY_PARAMS = new Set([
  "access_token",
  "api_key",
  "apikey",
  "auth",
  "code",
  "email",
  "id_token",
  "key",
  "otp",
  "passwd",
  "password",
  "phone",
  "pwd",
  "refresh_token",
  "secret",
  "sesame",
  "sig",
  "signature",
  "token"
]);

/** Query parameters whose value is itself a URL, so it can hide a token one level down. */
const NESTED_URL_PARAMS = new Set([
  "callback",
  "continue",
  "next",
  "redirect",
  "redirect_uri",
  "redirect_url",
  "return_to",
  "returnto",
  "url"
]);

/** Property names that hold a credential outright. */
const SECRET_PROPERTY_KEYS = new Set([
  "access_token",
  "api_key",
  "apikey",
  "code",
  "id_token",
  "key",
  "otp",
  "password",
  "refresh_token",
  "secret",
  "sesame",
  "signature",
  "token"
]);

/** Substrings that mark a property as holding a URL or path worth scrubbing. */
const URLISH_KEY_PARTS = [
  "url",
  "uri",
  "referrer",
  "referer",
  "path",
  "link",
  "redirect",
  "location"
];

const MAX_NESTING = 3;

function redactPath(path: string): string {
  const segments = path.split("/");
  let redactNext = false;
  return segments
    .map((segment) => {
      if (redactNext && segment) {
        redactNext = false;
        return REDACTED;
      }
      redactNext = SECRET_PATH_SEGMENTS.has(segment.toLowerCase());
      return segment;
    })
    .join("/");
}

function redactQuery(query: string, depth: number): string {
  if (!query) return query;
  const params = new URLSearchParams(query);
  const redacted = new URLSearchParams();
  params.forEach((value, name) => {
    const lowered = name.toLowerCase();
    if (SECRET_QUERY_PARAMS.has(lowered)) {
      redacted.append(name, REDACTED);
    } else if (NESTED_URL_PARAMS.has(lowered) && value) {
      redacted.append(name, sanitizeUrl(value, depth + 1));
    } else {
      redacted.append(name, value);
    }
  });
  return redacted.toString();
}

/** Return value with credential path segments and query parameters redacted. */
export function sanitizeUrl(value: string, depth = 0): string {
  if (typeof value !== "string" || !value) return value;
  if (depth > MAX_NESTING) return REDACTED;

  const hashIndex = value.indexOf("#");
  const hash = hashIndex >= 0 ? value.slice(hashIndex + 1) : "";
  const beforeHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const queryIndex = beforeHash.indexOf("?");
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";
  const path = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;

  // An OAuth-style fragment (#access_token=…) is a query string in disguise.
  const cleanHash = hash.includes("=") ? redactQuery(hash, depth) : hash;

  let sanitized = redactPath(path);
  if (queryIndex >= 0) sanitized += `?${redactQuery(query, depth)}`;
  if (hashIndex >= 0) sanitized += `#${cleanHash}`;
  return sanitized;
}

function isUrlishKey(key: string): boolean {
  return URLISH_KEY_PARTS.some((part) => key.includes(part));
}

/** Return a copy of properties with credentials and token-bearing URLs redacted. */
export function sanitizeProperties<T extends Record<string, unknown>>(
  properties: T,
  depth = 0
): T {
  if (!properties || depth > MAX_NESTING) return properties;
  const sanitized: Record<string, unknown> = {};
  Object.entries(properties).forEach(([key, value]) => {
    const lowered = key.toLowerCase().replace(/^\$+/, "");
    if (SECRET_PROPERTY_KEYS.has(lowered)) {
      sanitized[key] = REDACTED;
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      sanitized[key] = sanitizeProperties(value as Record<string, unknown>, depth + 1);
    } else if (typeof value === "string" && isUrlishKey(lowered)) {
      sanitized[key] = sanitizeUrl(value);
    } else {
      sanitized[key] = value;
    }
  });
  return sanitized as T;
}
